import { useId, useState } from "react";
import { Loader2 } from "lucide-react";
import {
  documentDisplayName,
  type BrainCategoryShelf,
  type MadeBy,
  type ProjectDocument,
} from "@gb/contracts";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useSetDocumentCategory } from "@/features/project/documents-api";
import { cn } from "@/lib/utils";
import { useBrainSummary } from "./brain-api";

/**
 * Who set the file's main category. A person's move is never undone by a
 * card written later, so it is worth saying which it was. `code` is not
 * written by anything today; it is named so the line never goes blank.
 */
const SET_BY: Record<MadeBy, string> = {
  person: "Moved by a person",
  model: "Sorted by the card",
  code: "Sorted automatically",
};

/** One name, as a small chip. */
function Chip({ name, muted }: { name: string; muted?: boolean }) {
  return (
    <li
      className={cn(
        "rounded-full border px-2 py-[2px] text-[0.74rem] font-semibold",
        muted ? "border-border-inner bg-surface text-muted" : "border-border bg-canvas text-ink-slate",
      )}
    >
      {name}
    </li>
  );
}

/**
 * The tenant's category a file lives in, the others it is also relevant to,
 * and a person's way to move it (`document-ai/0032`, 2026-10-03).
 *
 * It replaces the file's areas and their several-of picker. A file has one
 * main category, so this is a single choice, over the tenant's whole list:
 * an empty category is somewhere a file can go. The names come from the
 * server, in the tenant's own words; nothing here holds a copy of them.
 *
 * The other categories are the card's, and a move leaves them as they are.
 */
export function DocumentCategory({
  projectId,
  document,
}: {
  projectId: string;
  document: Pick<ProjectDocument, "id" | "title" | "filename" | "category" | "alsoRelevant">;
}) {
  const [choosing, setChoosing] = useState(false);
  // The Sources tab's own summary, so the list is the one its shelves show,
  // in the same order. Usually already read.
  const summary = useBrainSummary(projectId);
  const categories = summary.data?.categories ?? [];
  const name = document.title ?? documentDisplayName(document.filename);
  const { category, alsoRelevant } = document;

  return (
    <div className="mt-2.5 flex flex-col gap-1.5">
      <div className="flex flex-wrap items-center gap-2">
        {category ? (
          <>
            <ul aria-label={`Category of ${name}`} className="flex">
              <Chip name={category.name} />
            </ul>
            <span className="text-[0.78rem] text-muted">{SET_BY[category.madeBy]}</span>
          </>
        ) : (
          // Its card is not written, or it could not be read. A person can
          // still put it somewhere.
          <span className="text-[0.78rem] text-muted">Not sorted yet</span>
        )}
        {/* Only once the list is known: a picker over nothing is no choice. */}
        {categories.length > 0 && (
          <Button variant="secondary" size="sm" onClick={() => setChoosing(true)}>
            Move to…
          </Button>
        )}
      </div>
      {alsoRelevant.length > 0 && (
        // Chips, not a sentence: a tenant's names can hold commas of their own
        // ("Electrical, earthing and cabling").
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-[0.78rem] text-muted">Also relevant to</span>
          <ul aria-label={`Also relevant to, for ${name}`} className="flex flex-wrap gap-1.5">
            {alsoRelevant.map((other) => (
              <Chip key={other.key} name={other.name} muted />
            ))}
          </ul>
        </div>
      )}
      {choosing && (
        <CategoryPickerDialog
          projectId={projectId}
          documentId={document.id}
          current={category?.key ?? null}
          name={name}
          categories={categories}
          onClose={() => setChoosing(false)}
        />
      )}
    </div>
  );
}

/** The choice itself: one of the tenant's categories. Exported for its test. */
export function CategoryPickerDialog({
  projectId,
  documentId,
  current,
  name,
  categories,
  onClose,
}: {
  projectId: string;
  documentId: string;
  /** The key of the file's main category, or null when it has none. */
  current: string | null;
  name: string;
  /** Every one of the tenant's categories, in its order, empty ones included. */
  categories: readonly Pick<BrainCategoryShelf, "key" | "name" | "description">[];
  onClose: () => void;
}) {
  const ids = useId();
  const [chosen, setChosen] = useState<string | null>(current);
  const save = useSetDocumentCategory(projectId);
  // Moving a file to where it already is would write a line to the log that
  // says nothing happened.
  const unchanged = chosen === null || chosen === current;

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent size="lg">
        <DialogHeader>
          <div className="min-w-0">
            <DialogTitle>Move to…</DialogTitle>
            <DialogDescription className="mt-1">
              Choose the category “{name}” belongs in. A card written later won’t move it back.
            </DialogDescription>
          </div>
        </DialogHeader>
        <DialogBody>
          <fieldset className="flex flex-col gap-2">
            <legend className="sr-only">Category for {name}</legend>
            {categories.map((category) => {
              const picked = chosen === category.key;
              return (
                <label
                  key={category.key}
                  className={cn(
                    "flex cursor-pointer items-start gap-2.5 rounded-input border-[1.5px] px-3.5 py-2.5 transition-colors",
                    picked ? "border-brand bg-brand-tint" : "border-border bg-surface",
                  )}
                >
                  <input
                    type="radio"
                    name={`${ids}-category`}
                    value={category.key}
                    checked={picked}
                    onChange={() => setChosen(category.key)}
                    aria-labelledby={`${ids}-${category.key}-name`}
                    aria-describedby={`${ids}-${category.key}-description`}
                    className="mt-[3px] size-4 shrink-0 accent-brand"
                  />
                  <span className="min-w-0">
                    <span className="block text-[0.9rem] font-semibold text-ink">
                      <span id={`${ids}-${category.key}-name`}>{category.name}</span>
                      {category.key === current && (
                        <span className="font-normal text-muted"> · where it is now</span>
                      )}
                    </span>
                    <span
                      id={`${ids}-${category.key}-description`}
                      className="block text-[0.82rem] leading-[1.45] text-muted"
                    >
                      {category.description}
                    </span>
                  </span>
                </label>
              );
            })}
          </fieldset>
          {save.error && (
            <p role="alert" className="mt-3 text-[0.85rem] font-semibold text-danger">
              {save.error.message}
            </p>
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={onClose} disabled={save.isPending}>
            Cancel
          </Button>
          <Button
            className="hover:bg-brand-deep"
            disabled={unchanged || save.isPending}
            onClick={() => {
              if (chosen === null) return;
              save.mutate({ documentId, category: chosen }, { onSuccess: onClose });
            }}
          >
            {save.isPending ? <Loader2 size={16} className="animate-spin" /> : null}
            Move
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
